import type { ParsedCommand } from './types';

/**
 * Tokenize a raw `GlobalMapSearch` input into a `ParsedCommand`. Pure
 * function — call it as often as you like (every keystroke is fine).
 *
 * Behavior:
 *   - `/`             → empty `name`, no args. Surface the full
 *                       command catalog from this state.
 *   - `/foo`          → `name = 'foo'`, no args.
 *   - `/foo `         → `name = 'foo'`, no args, `trailingSpace = true`.
 *                       Used to detect "user is past the name and into
 *                       args" without needing a non-whitespace arg
 *                       character to be typed first.
 *   - `/foo bar baz`  → `name = 'foo'`, `args = ['bar', 'baz']`,
 *                       `rest = 'bar baz'`.
 *
 * Inputs not starting with `/` should never reach this — the host is
 * expected to gate on that — but for safety the parser still produces
 * a deterministic empty-shape result instead of throwing.
 */
export function parseCommand(input: string): ParsedCommand {
  const raw = input;

  if (!input.startsWith('/')) {
    return {
      raw,
      name: '',
      args: [],
      rest: '',
      trailingSpace: false,
    };
  }

  // Drop the leading slash. We intentionally do *not* trim here: a
  // single trailing space after the name is meaningful (`trailingSpace`)
  // and trimming would erase it.
  const body = input.slice(1);

  // Match the first whitespace-bounded token. This is more forgiving
  // than `body.split(/\s+/)` because it preserves the rest verbatim,
  // which is what `rest` (and scoped-search bridging) needs.
  const nameMatch = body.match(/^(\S*)(\s*)(.*)$/);
  if (!nameMatch) {
    return {
      raw,
      name: '',
      args: [],
      rest: '',
      trailingSpace: false,
    };
  }

  const [, nameTokenRaw, spacing, remainder] = nameMatch;
  const name = nameTokenRaw.toLowerCase();
  const trailingSpace = spacing.length > 0;

  // For `rest` we want the user-typed remainder with its internal
  // whitespace preserved but with the surrounding whitespace cleaned
  // up so a downstream `/transcript hello` doesn't pass through with
  // a leading space.
  const rest = remainder.trim();

  // Positional args use a simple whitespace split. Quoted-arg support
  // is not part of Tier 1 — when commands need it (e.g. `/sms`), the
  // command def can re-tokenize `rest` with its own rules.
  const args = rest.length > 0 ? rest.split(/\s+/) : [];

  return {
    raw,
    name,
    args,
    rest,
    trailingSpace,
  };
}
